import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { useAuditTrail } from '../hooks/useAuditTrail';
import { Option } from '../../common/components/Inputs/SelectWithSearch/SelectWithSearch';
import { DateRangeValuePiece } from '../../common/components/Inputs/DateRangePickerMui/DateRangePickerMui';
import { parseDateToRequest } from '../../common/components/Inputs/DateRangePickerMui/DateRangePickerMui.util';
import { ConfigurableTable } from 'src/common/components/ConfigurableTable/ConfigurableTable';
import { ColumnConfiguration } from 'src/common/components/ConfigurableTable/models/configurableTable';
import { TextCell } from 'common/components/ConfigurableTable/Components/Cells/TextCell';
import { DateTimeCell } from 'common/components/ConfigurableTable/Components/Cells/DateTimeCell';
import { ContentBox } from 'common/components/AppLayout/ContentBox.tsx';
import { TableSkeleton } from 'common/components/Table/TableSkeleton.tsx';
export const AuditTrailTable = ({
  dateRange,
  iccid,
  selectedUsername,
  selectedField,
}: {
  iccid: string;
  dateRange: [DateRangeValuePiece, DateRangeValuePiece];
  selectedUsername: Option | null;
  selectedField: string[];
}) => {
  const { t } = useTranslation();

  const {
    data: dataAuditTrail,
    pagination: auditTrailsPagination,
    fetchStatus,
  } = useAuditTrail(
    iccid,
    selectedUsername?.label as string,
    parseDateToRequest(dateRange?.[0]),
    parseDateToRequest(dateRange?.[1]),
    selectedField,
  );

  const configuration: ColumnConfiguration[] = [
    {
      field: 'propertyName',
      headerName: t('auditTrail.tableHeaders.field'),
      resizable: false,
      sortable: false,
      pinnable: false,
      width: 220,
      minWidth: 70,
      maxWidth: 300,
    },
    {
      field: 'oldValue',
      headerName: t('auditTrail.tableHeaders.changedFrom'),
      resizable: false,
      sortable: false,
      pinnable: false,
      width: 300,
      minWidth: 70,
      maxWidth: 300,
    },
    {
      field: 'newValue',
      headerName: t('auditTrail.tableHeaders.changedTo'),
      resizable: false,
      sortable: false,
      pinnable: false,
      width: 300,
      minWidth: 70,
      maxWidth: 300,
    },
    {
      field: 'createdAt',
      headerName: t('auditTrail.tableHeaders.effectiveTime'),
      resizable: false,
      sortable: false,
      pinnable: false,
      width: 170,
      minWidth: 70,
      maxWidth: 170,
    },
    {
      field: 'sourceName',
      headerName: t('auditTrail.tableHeaders.source'),
      resizable: false,
      sortable: false,
      pinnable: false,
      width: 260,
      minWidth: 70,
      maxWidth: 300,
    },
    {
      field: 'changedBy',
      headerName: t('auditTrail.tableHeaders.userName'),
      resizable: false,
      sortable: false,
      pinnable: false,
      width: 240,
      minWidth: 70,
      maxWidth: 300,
    },
  ];

  return fetchStatus === 'fetching' ? (
    <ContentBox>
      <TableSkeleton showStartSkeleton showEndSkeleton numberOfRows={20} />
    </ContentBox>
  ) : (
    <ConfigurableTable
      data-testid="audit trail table"
      getRowId={() => uuidv4()}
      disableColumnReorder
      disableVirtualization
      configuration={configuration}
      cellConfiguration={{
        createdAt: DateTimeCell,
        default: TextCell,
      }}
      rows={dataAuditTrail}
      totalNumberOfItems={auditTrailsPagination.totalNumberOfItems}
      onPaginationChange={(pagination) => {
        if (pagination.page !== auditTrailsPagination.page) {
          auditTrailsPagination.onPageChange(pagination.page);
        }

        if (pagination.pageSize !== auditTrailsPagination.perPage) {
          auditTrailsPagination.onRowsPerPageChange(pagination.pageSize);
        }
      }}
      page={auditTrailsPagination.page}
      pageSize={auditTrailsPagination.perPage}
    />
  );
};
